import { ListCache, safeJsonParse } from '@halo/utils';
import { type User } from '../sdk/types';
import { formatUser } from '../utils';
import type { Client } from '../client';
import { AbstractApi } from './Api';
import type { UploadAvatarPreSignBody } from './common';

export class UserApi extends AbstractApi {
  private caches: {
    baseUsersCache: ListCache<User, User>;
    usersProfileCache: ListCache<User, any>;
  } = {} as any;
  constructor(client: Client) {
    super(client);

    // users base info cache
    this.caches.baseUsersCache = new ListCache({
      idField: 'user_id',
      fetchData: (ids) => client.invoke('resolveUsersInfo', [ids, true]),
      formatData: formatUser,
    });

    this.client.addEventListener('ContactInfoListChanged', ({ detail: { data } }) => {
      this.caches.baseUsersCache.updateCache(data);
    });

    // Users profile cache
    this.caches.usersProfileCache = new ListCache({
      idField: 'user_id',
      fetchData: (ids) =>
        client.invoke('getUserProfiles', [ids[0], true]).then((data) => {
          data.user_id = ids[0];
          return [data];
        }),
      formatData: (data) => {
        return data?.username ? formatUser(data) : null;
      },
    });

    this.client.addEventListener('UserStateChanged', async ({ detail: { status, user_id } }) => {
      const data = { state: status };
      await this.dispatchSetUserData(user_id, data);
      await this.dispatchSetUserProfile(user_id, data);
    });

    this.client.addEventListener(
      'UserProfileChanged',
      ({ detail: { user_id, update_profile } }) => {
        const user = {} as User;
        update_profile.forEach((value, key) => {
          user[key] = value;
        });
        user.user_id = user_id;
        const oldUser = this.caches.usersProfileCache.getCacheData(user_id);
        const oldBaseUsers = this.caches.baseUsersCache.getCacheData(user_id);
        this.caches.usersProfileCache.updateCache([{ ...oldUser, ...user }]);
        this.caches.baseUsersCache.updateCache([{ ...oldBaseUsers, ...user }]);
      }
    );
  }
  uploadSignalPackage() {
    return this.client.invoke('uploadSignalPackage');
  }
  uploadGroupPackage() {
    return this.client.invoke('uploadGroupPackage');
  }
  retryPackage(ids?: string[]) {
    if (ids) {
      return this.client.invoke('retryErrorsByIds', [ids]);
    }
    return this.client.invoke('retryAllErrors');
  }
  setUserData(key: string, value: string) {
    return this.client.invoke('setUserData', [key, value]);
  }
  getUserData(key: string) {
    return this.client.invoke('getUserData', [key]);
  }
  async dispatchSetUserData(userId: string, data: Record<string, any>) {
    const { promise, removeListener } = this.resolveUsersInfo(userId, (user) => {
      const user_ = { ...user, ...data };
      this.client.dispatchEvent('ContactInfoListChanged', {
        data: [user_],
      });
    });
    await promise;
    removeListener();
  }
  dispatchSetUserProfile(userId: string, data: Record<string, any>) {
    this.client.dispatchEvent('UserProfileChanged', {
      user_id: userId,
      update_profile: new Map(Object.entries(data)),
    });
  }
  async setUserName(firstName: string, lastName: string) {
    await this.client.invoke('setUserName', [firstName, lastName]);

    const data = { first_name: firstName, last_name: lastName };

    await this.dispatchSetUserData(this.client.userId, data);
    await this.dispatchSetUserProfile(this.client.userId, data);
  }
  async setUserNameId(userNameId: string) {
    await this.client.invoke('setUserNameId', [userNameId]);

    const data = { username: userNameId };

    await this.dispatchSetUserData(this.client.userId, data);
    await this.dispatchSetUserProfile(this.client.userId, data);
  }
  async setUserProps(data: Record<string, any>) {
    await this.client.invoke('setUserProps', [data]);

    await this.dispatchSetUserData(this.client.userId, data);
    await this.dispatchSetUserProfile(this.client.userId, data);
  }
  async getUserProps() {
    const data = await this.client.invoke('getUserProps');
    return data as unknown as User;
  }
  async setExtendUserProfiles(data: Record<string, any>) {
    await this.client.invoke('setExtendUserProfiles', [data]);

    await this.dispatchSetUserProfile(this.client.userId, data);
  }
  getUserProfiles(userId: string, cb: (data: User) => void) {
    const promise = this.caches.usersProfileCache.getData([userId], true).then((res) => res[0]);

    let removeListener: () => void;

    const profilePromise = new Promise((resolve) => {
      promise.then((result) => {
        if (result.user_id) {
          cb(result);
          resolve(undefined);
        }
      });
      removeListener = this.client.addEventListener(
        'UserProfileChanged',
        async ({ detail: { user_id } }) => {
          if (user_id !== userId) {
            return;
          }
          const result = this.caches.usersProfileCache.getCacheData(user_id);
          cb(result);
          resolve(undefined);
        }
      );
    });

    return {
      promise: Promise.all([promise, profilePromise]).then(() => undefined),
      removeListener,
    };
  }
  async uploadAvatar({ file, ...rest }: { file: File } & UploadAvatarPreSignBody) {
    const access_url = await this.client.Api.common.getUploadAvatar({
      file,
      ...rest,
    });
    await this.client.invoke('uploadAvatar', [access_url]);

    const data = { avatar: access_url };
    await this.dispatchSetUserData(this.client.userId, data);
    await this.dispatchSetUserProfile(this.client.userId, data);

    return access_url;
  }
  resolveUsersInfo(
    userId: string,
    cb: (data: User) => void
  ): { promise: Promise<void>; removeListener: () => void };

  resolveUsersInfo(
    userIds: string[],
    cb: (data: User[]) => void
  ): { promise: Promise<void>; removeListener: () => void };

  resolveUsersInfo(
    userIds: string | string[],
    cb: ((data: User) => void) | ((data: User[]) => void)
  ) {
    const isArray = Array.isArray(userIds);
    const userIds_ = isArray ? userIds : [userIds];

    const getUsers = () => {
      return this.caches.baseUsersCache.getData(userIds_).then((users) => {
        cb((isArray ? users : users[0]) as any);
      });
    };

    const promise = getUsers();

    const removeListener = this.client.addEventListener('ContactInfoListChanged', () => {
      getUsers();
    });

    return {
      promise,
      removeListener,
    };
  }
  getUsersInfo(userId: string): Promise<User | undefined>;

  getUsersInfo(userIds: string[]): Promise<User[]>;

  getUsersInfo(userIds: string | string[]): Promise<User | User[]> {
    return new Promise((resolve, reject) => {
      const { promise, removeListener } = this.resolveUsersInfo(userIds as any, (users) => {
        resolve(users);
        removeListener();
      });
      promise.catch(reject);
    });
  }

  /**
   * Dispose resources and cleanup timer
   */
  dispose(): void {
    for (const cache of Object.values(this.caches)) {
      cache.dispose();
    }
  }
  queryUserInfoGlobal(user_id: string) {
    return this.client.invoke('getUserProfiles', [user_id, true]) as unknown as Promise<any>;
  }
  async uploadUserProfile(data: Array<Record<string, any>>) {
    try {
      await this.client.invoke('uploadUserProfile', [data]);
      const extractedData = data.reduce((acc, item) => {
        Object.assign(acc, item.data);
        return acc;
      }, {});
      await this.dispatchSetUserData(this.client.userId, extractedData);
      await this.dispatchSetUserProfile(this.client.userId, extractedData);
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(
        safeJsonParse(safeJsonParse(e.message, { error: '' }).error, {
          message: 'Network error',
        }).message
      );
    }
  }
}
